import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useLayoutEffect as useReactLayoutEffect,
	useState,
} from "react";
import { CookiesSnack } from "~ui-components/components/organisms/cookies-snack";

export type AnalyticsConsent = "denied" | "granted";

interface AnalyticsConsentContextValue {
	consent: AnalyticsConsent;
	hasChoice: boolean;
	setConsent: (consent: AnalyticsConsent) => void;
}

const AnalyticsConsentContext =
	createContext<AnalyticsConsentContextValue | null>(null);

const getStoredConsent = (): AnalyticsConsent =>
	localStorage.getItem("cookies:accepted") === "true" ? "granted" : "denied";

const updateMangoConsent = (consent: AnalyticsConsent) => {
	if (!window.gtag) return;
	const value = consent === "granted" ? "granted" : "denied";
	window.gtag("consent", "update", {
		ad_storage: value,
		analytics_storage: value,
		ad_user_data: value,
		ad_personalization: value,
	});
};

const setDefaultMangoConsent = () => {
	if (!window.gtag) return;
	window.gtag("consent", "default", {
		ad_storage: "denied",
		analytics_storage: "denied",
		ad_user_data: "denied",
		ad_personalization: "denied",
		wait_for_update: 500,
	});
};

export const AnalyticsConsentProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const [consent, setConsentState] = useState<AnalyticsConsent>("denied");
	const [hasChoice, setHasChoice] = useState(false);

	useLayoutEffect(() => {
		const storedChoice = localStorage.getItem("cookies:accepted");
		const storedConsent = getStoredConsent();
		setDefaultMangoConsent();
		setConsentState(storedConsent);
		setHasChoice(storedChoice !== null);
		if (storedConsent === "granted") updateMangoConsent(storedConsent);
	}, []);

	const setConsent = (nextConsent: AnalyticsConsent) => {
		localStorage.setItem("cookies:accepted", String(nextConsent === "granted"));
		setConsentState(nextConsent);
		setHasChoice(true);
		updateMangoConsent(nextConsent);
	};

	return (
		<AnalyticsConsentContext.Provider
			value={{ consent, hasChoice, setConsent }}>
			{children}
		</AnalyticsConsentContext.Provider>
	);
};

export const useAnalyticsConsent = (): AnalyticsConsentContextValue => {
	const context = useContext(AnalyticsConsentContext);
	if (!context) {
		throw new Error(
			"useAnalyticsConsent must be used within AnalyticsConsentProvider",
		);
	}
	return context;
};

const CookiesBanner = () => {
	const [client, setClient] = useState(false);
	const { hasChoice, setConsent } = useAnalyticsConsent();

	useEffect(() => setClient(true), []);

	if (!client) return null;

	return (
		<CookiesSnack
			open={!hasChoice}
			onAccept={() => setConsent("granted")}
			onClose={() => setConsent("denied")}
		/>
	);
};

export const Cookies = () => {
	const context = useContext(AnalyticsConsentContext);
	if (context) return <CookiesBanner />;

	return (
		<AnalyticsConsentProvider>
			<CookiesBanner />
		</AnalyticsConsentProvider>
	);
};

const useLayoutEffect =
	typeof window !== "undefined" ? useReactLayoutEffect : useEffect;
